<template>
  <div>
    <p class="pl-1">
      <v-icon class="mr-2">extension</v-icon>Need to add an app feature to this customer's account?
      <v-btn
        rounded
        outlined
        text
        x-small
        class="ml-2"
        @click="showDialog"
      >Click here</v-btn>
    </p>
    <v-dialog v-model="dialog.show" persistent :overlay="false" max-width="600px">
      <v-card>
        <v-card-title class="title secondary mb-4">Add App Feature</v-card-title>
        <v-card-text>
          <p>
            Select a product that has app features. This will create a Shopify order for the customer.
          </p>
          <v-autocomplete
            v-model="dialog.product"
            :items="featureProducts"
            dense
            filled
            label="Products with App Features"
            item-text="title"
            item-value="id"
            return-object
            :loading="loading"
          ></v-autocomplete>

          <div v-if="dialog.product">
            <p>
              Product ID:
              <a :href="`https://${storeUrl}/admin/products/${dialog.product.id}`" target="_blank">
                {{ dialog.product.id }}
              </a>
            </p>
            <p>
              Features: {{ getProductFeatures(dialog.product.id) }}
            </p>
            <v-alert type="info" dense class="mt-2">
              Note: There will be a short delay before the app feature appears in the customer's account while the Shopify webhook gets processed. Refresh to see updates.
            </v-alert>
            <v-btn
              color="primary"
              class="mt-2"
              @click="createOrder"
              :loading="saving"
              :disabled="saving || !dialog.product.variants || !dialog.product.variants.length"
            >
              <v-icon left>add_shopping_cart</v-icon>
              Create Order
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="dialog.show = false"
          >Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snack = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import fb from "@/main";

export default {
  name: "AddCustomerFeature",
  props: {
    shopifyCustomerId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      store: "soundpaint",
      loading: false,
      saving: false,
      dialog: {
        show: false,
        product: null,
      },
      allFeatures: [],
      products: [],
      snack: false,
      snackColor: "",
      snackText: "",
    };
  },
  computed: {
    storeUrl() {
      return "soundpaint.myshopify.com";
    },
    featureProducts() {
      const featureProductIds = new Set(
        this.allFeatures.map(f => String(f.ShopifyProductId))
      );
      return this.products.filter(p => featureProductIds.has(String(p.id)));
    },
  },
  async created() {
    await this.loadData();
  },
  methods: {
    getProductFeatures(productId) {
      return this.allFeatures
        .filter(f => String(f.ShopifyProductId) === String(productId))
        .map(f => f.Feature)
        .join(', ');
    },
    async loadData() {
      this.loading = true;
      try {
        await Promise.all([
          this.getAppFeatures(),
          this.getProducts(),
        ]);
      } catch (error) {
        console.error(error);
      }
      this.loading = false;
    },
    async getAppFeatures() {
      const getAppFeaturesCall = fb.functions.httpsCallable("getAppFeatures");
      const { data: recordset } = await getAppFeaturesCall({
        store: this.store,
      });
      if (recordset && recordset.length) {
        this.allFeatures = recordset;
      }
    },
    async getProducts() {
      const getProductsCall = fb.functions.httpsCallable("getProducts");
      const { data: recordset } = await getProductsCall({
        store: this.store,
      });
      if (recordset && recordset.length) {
        this.products = recordset;
      }
    },
    async createOrder() {
      if (!this.dialog.product || !this.shopifyCustomerId) {
        return;
      }
      const variantId = this.dialog.product.variants?.[0]?.id;
      if (!variantId) {
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "No variant found for this product.";
        return;
      }
      this.saving = true;
      try {
        const createFeatureOrderCall = fb.functions.httpsCallable("createFeatureOrder");
        await createFeatureOrderCall({
          store: this.store,
          shopifyCustomerId: this.shopifyCustomerId,
          variantId: variantId,
          productTitle: this.dialog.product.title,
        });
        this.dialog.show = false;
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Order created successfully. The app feature will appear after the webhook processes.";
      } catch (error) {
        console.error(error);
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "Error creating order: " + (error.message || "Unknown error");
      }
      this.saving = false;
    },
    showDialog() {
      this.dialog = {
        show: true,
        product: null,
      };
    },
  },
};
</script>
